import { useEffect, useState, useRef } from "react"

import { ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"

import { cn } from "@lib/utils"

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui/collapsible"
import { Input } from "@ui/input"
import { Slider } from "@ui/slider"
import { Textarea } from "@ui/textarea"

const DEBOUNCE_DELAY = 1000

interface SearchSectionProps {
    label: string
    placeholder: string
    Icon?: LucideIcon
    onSelection?: (selection: any) => void
    onClear: number
}
export function SearchSection({ label, placeholder, Icon, onSelection, onClear }: Readonly<SearchSectionProps>) {
    const [vectorText, setVectorText] = useState<string>("")
    const [threshold, setThreshold] = useState<number>(0.5)

    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

    const handleOnSelectionChange = () => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        if (!vectorText?.length) {
            onSelection?.(undefined)
        } else {
            onSelection?.({ value: vectorText, threshold })
        }
    }

    const handleVectorTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setVectorText(value)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (!value?.length) {
                onSelection?.(undefined)
            } else {
                onSelection?.({ value, threshold })
            }
        }, DEBOUNCE_DELAY)
    }

    const handleThresholdUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value

        let newThreshold: number
        if (val === "") {
            newThreshold = 0
        } else {
            const num = parseFloat(val)
            if (isNaN(num)) {
                newThreshold = 0
            } else if (num < 0) {
                newThreshold = 0
            } else if (num > 1) {
                newThreshold = 1
            } else {
                newThreshold = num
            }
        }

        setThreshold(newThreshold)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (!vectorText?.length) {
                onSelection?.(undefined)
            } else {
                onSelection?.({ value: vectorText, threshold: newThreshold })
            }
        }, DEBOUNCE_DELAY)
    }

    const handleSliderChange = (value: number[]) => {
        const newThreshold = value[0]
        setThreshold(newThreshold)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }

        debounceTimerRef.current = setTimeout(() => {
            if (!vectorText?.length) {
                onSelection?.(undefined)
            } else {
                onSelection?.({ value: vectorText, threshold: newThreshold })
            }
        }, DEBOUNCE_DELAY)
    }

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        e.stopPropagation()
        if (e.key === "Enter") {
            e.preventDefault()
            handleOnSelectionChange()
        }
    }

    useEffect(() => {
        setVectorText("")
        setThreshold(0.5)

        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current)
        }
    }, [onClear])

    useEffect(() => {
        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
            }
        }
    }, [])

    return (
        <div className={cn("relative flex w-full min-w-0 flex-col p-2 border-b border-sidebar-border")}>
            <Collapsible defaultOpen={false} className="group/collapsible">
                <CollapsibleTrigger className={cn("group/label w-full text-sm text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground", "flex h-8 shrink-0 items-center rounded-md px-2 text-sm font-medium outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0", "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0 hover:cursor-pointer mb-1")}>
                    {Icon && <Icon className="mr-2" />}
                    {label} <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <Textarea
                        value={vectorText}
                        onChange={handleVectorTextChange}
                        onKeyDown={handleInputKeyDown}
                        placeholder={placeholder}
                    />
                    {Boolean(vectorText?.length) && (
                        <div className="flex flex-row gap-2 items-center py-2 mt-0">
                            <Slider
                                value={[threshold]}
                                onValueChange={handleSliderChange}
                                max={1}
                                step={0.1}
                            />
                            <Input
                                min="0"
                                max="1"
                                step="0.1"
                                type="number"
                                value={threshold}
                                onChange={handleThresholdUpdate}
                                className="border rounded-md p-2 w-fit"
                            />
                        </div>
                    )}
                </CollapsibleContent>
            </Collapsible>
        </div>
    )
}